import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getEventClient } from '~/lib/event'
import {
  CampaignCategory,
  CampaignScheduleType,
  CopyCampaignFragment,
  useCreateCampaignMutation,
  WaitingCampaignListDocument,
  AvailableTicketsDocument,
  AvailableAndRevisionCampaignsDocument,
  useViewerQuery,
  PaymentType,
  TicketModelCategory,
  IssuedTicketsDocument,
} from '~/generated/graphql'
import { ApolloError } from '@apollo/client'
import { eventPush } from '~/lib/gtmHelper'
import { getPlatform } from '~/lib/platform'
import { v4 as uuidv4 } from 'uuid'
import { isExpenseCampaignAllowed as checkExpenseCampaignAllowed } from '~/lib/feature/expenseCampaignAllowed'
import { useAuth } from './AuthContext'
import { resetWebVitalsMetrics } from '~/lib/web-vitals'
import { waitingCampaignInput } from '~/features/campaigns/WaitingCampaignList'
import {
  convertCampaignToCreateCampaignState,
  createCampaignInitialState,
  CreateCampaignState,
  convertCreateCampaignStateToCampaignCreateParam,
} from '~/features/campaign_input/state'
import { logger } from '~/lib/logger'
import { useRouter } from 'next/router'
import { decodeId } from '~/lib/convertId'

export const campaignCreationMethods = {
  copy: 0,
  new: 1,
} as const

export const pageSteps = {
  createMethod: 'create_method',
  storeInfo: 'store_info',
  serviceContents: 'service_contents',
  snsContents: 'sns_contents',
  influencerContactInfo: 'influencer_contact_info',
  grossContents: 'gross_contents',
  reservationScheduleContents: 'reservation_schedule_contents',
  expenseSettings: 'expense_settings',
  publishSettings: 'publish_settings',
  confirmation: 'confirmation',
} as const

export type PageStepType = (typeof pageSteps)[keyof typeof pageSteps]
export type PageResultType = 'create_success' | 'create_failure'

export const pageStepItemCounts = {
  [pageSteps.createMethod]: 0,
  [pageSteps.storeInfo]: 7,
  [pageSteps.serviceContents]: 8,
  [pageSteps.expenseSettings]: 2,
  [pageSteps.snsContents]: 5,
  [pageSteps.influencerContactInfo]: 4,
  [pageSteps.grossContents]: 2,
  [pageSteps.reservationScheduleContents]: 1,
  [pageSteps.publishSettings]: 2,
  [pageSteps.confirmation]: 0,
}

export const createPageStepSequence = ({
  category,
  scheduleType,
}: {
  category: CampaignCategory
  scheduleType: CampaignScheduleType
}) => {
  const commonSteps = [
    pageSteps.expenseSettings,
    pageSteps.serviceContents,
    pageSteps.influencerContactInfo,
    pageSteps.snsContents,
    pageSteps.grossContents,
  ]

  if (category === CampaignCategory.Service) {
    // scheduleTypeがNOT_USEDの場合、日程調整の設定は不要なのでページに遷移させない
    if (scheduleType === CampaignScheduleType.NotUsed) {
      return [
        pageSteps.createMethod,
        pageSteps.storeInfo,
        ...commonSteps,
        pageSteps.publishSettings,
        pageSteps.confirmation,
      ]
    }
    return [
      pageSteps.createMethod,
      pageSteps.storeInfo,
      ...commonSteps,
      pageSteps.reservationScheduleContents,
      pageSteps.publishSettings,
      pageSteps.confirmation,
    ]
  } else {
    return [pageSteps.createMethod, ...commonSteps, pageSteps.publishSettings, pageSteps.confirmation]
  }
}

export const STEP = {
  NEXT: 'next',
  PREV: 'prev',
} as const

export type StepDirection = (typeof STEP)[keyof typeof STEP]

// PageStepTypeから'store_info'を除いた型
// note: 計測イベントではstore_infoを送信しない
export type PageEventType = Exclude<PageStepType | PageResultType, 'store_info'>

type CampaignCreationType = (typeof campaignCreationMethods)[keyof typeof campaignCreationMethods]
type ContextValue = {
  campaign: CreateCampaignState
  pageStep: PageStepType | undefined
  progress: number
  campaignCreateErrors: ApolloError | null
  setCampaign: Dispatch<SetStateAction<CreateCampaignState>>
  changePageStep: (direction: StepDirection) => void
  pageResult: PageResultType | undefined
  setPageResult: (result: PageResultType | undefined) => void
  sendPageEvent: (page: PageEventType) => void
  // apolloerror
  setCampaignCreateErrors: (error: ApolloError | null) => void
  // 復元
  copyCampaign: (campaign: CopyCampaignFragment) => void
  resetCampaign: () => void
  creationType: CampaignCreationType
  isExpenseCampaignAllowed: boolean
  handleSkipExpenseInfoDialog: (shouldSaveToStorage: boolean) => void
  shouldOpenExpenseInfoDialog: () => boolean
  sessionId: string
}

export const CreateCampaignContext = createContext({} as ContextValue)

const SKIP_EXPENSE_INFO_DIALOG_KEY = 'skip_expense_info_dialog'

export const CreateCampaignProvider = (props: { children: React.ReactNode }) => {
  const { currentUser } = useAuth()
  const { data: viewerData } = useViewerQuery()
  const paymentType = viewerData?.viewer.company?.paymentType
  const isExpenseCampaignAllowed = checkExpenseCampaignAllowed(paymentType)

  const [campaign, setCampaign] = useState<CreateCampaignState>(createCampaignInitialState)
  const [pageStepIndex, setPageStepIndex] = useState<number>(0)
  const [pageResult, setPageResult] = useState<PageResultType | undefined>(undefined)
  const [sentEvents, setSentEvents] = useState<PageEventType[]>([])
  const [eventUuid] = useState<string>(uuidv4())
  const [sessionId] = useState<string>(uuidv4())
  const [creationType, setCreationType] = useState<CampaignCreationType>(campaignCreationMethods.new)
  const [campaignCreateErrors, setCampaignCreateErrors] = useState<ApolloError | null>(null)

  const platform = useMemo(() => getPlatform(), [])

  const router = useRouter()

  // 精算形式に関する補足情報のダイアログの表示制御を更新するための関数
  const handleSkipExpenseInfoDialog = (shouldSaveToStorage: boolean) => {
    if (shouldSaveToStorage) {
      try {
        localStorage.setItem(SKIP_EXPENSE_INFO_DIALOG_KEY, 'true')
      } catch (error) {
        console.error('localStorageの保存に失敗しました', error)
      }
    }

    const newQuery = {
      ...router.query,
      skipExpenseInfoDialog: 'true',
    }

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      // ページ遷移を発生させずにURLのみを更新する
      { shallow: true },
    )
  }

  // 精算形式に関する補足情報のダイアログを表示すべきかを判定する関数
  const shouldOpenExpenseInfoDialog = () => {
    try {
      // 半永久的に出さない
      const skipFromStorage = localStorage.getItem(SKIP_EXPENSE_INFO_DIALOG_KEY) === 'true'
      // クエリパラメータでスキップが指定されている場合は出さない
      const skipFromQuery = router.query.skipExpenseInfoDialog === 'true'
      return !(skipFromStorage || skipFromQuery)
    } catch (error) {
      console.error('localStorageの取得に失敗しました', error)
      return true // エラー時でもダイアログは出す
    }
  }

  const resetCampaign = () => {
    setCampaign(createCampaignInitialState)
  }

  useEffect(() => {
    if (currentUser.email) {
      setCampaign((prev) => ({
        ...prev,
      }))
    }
  }, [currentUser.email])

  // フォーム情報が更新されるたびにシーケンスを再生成する
  const pageSequence = useMemo(
    () =>
      createPageStepSequence({
        category: campaign.category ?? CampaignCategory.Service,
        scheduleType: campaign.scheduleType,
      }),
    [campaign],
  )

  // シーケンスとインデックスの変更時に現在のステップを再計算する
  const [pageStep, progress] = useMemo(() => {
    const newPageStep = pageSequence[pageStepIndex]
    if (!newPageStep) {
      return [undefined, 0]
    }
    // ページ遷移時に進捗を更新する
    const itemCounts = pageSequence.map((step) => pageStepItemCounts[step])
    const totalItemCount = itemCounts.reduce((acc, count) => acc + count, 0)
    const currentItemCount = itemCounts.slice(0, pageStepIndex).reduce((acc, count) => acc + count, 0)
    return [newPageStep, Math.floor((currentItemCount / totalItemCount) * 100)]
  }, [pageSequence, pageStepIndex])

  const changePageStep = (direction: StepDirection) => {
    if (!pageStep) {
      return
    }
    const currentIndex = pageSequence.indexOf(pageStep)
    if (currentIndex === -1) {
      return
    }
    const newIndex = direction === STEP.NEXT ? currentIndex + 1 : currentIndex - 1
    if (newIndex < 0 || newIndex >= pageSequence.length) {
      return
    }
    setPageStepIndex(newIndex)
  }

  useEffect(() => {
    if (pageStep) {
      resetWebVitalsMetrics(pageStep)
    }
  }, [pageStep])

  const sendPageEvent = useCallback(
    (page: PageEventType) => {
      if (sentEvents.includes(page)) {
        return
      }

      eventPush('create_campaign', {
        page: page,
        uuid: eventUuid,
        platform: platform,
      })
      setSentEvents((prev) => [...prev, page])
    },
    [sentEvents, eventUuid, platform],
  )

  useEffect(() => {
    if (pageStep && pageStep !== pageSteps.storeInfo) {
      sendPageEvent(pageStep)
    }
  }, [pageStep, sendPageEvent])

  const copyCampaign = async (campaign: CopyCampaignFragment) => {
    try {
      setCreationType(campaignCreationMethods.copy)
      setCampaign(convertCampaignToCreateCampaignState(campaign))
    } catch (e) {
      window.alert(e)
      return
    }
  }

  return (
    <CreateCampaignContext.Provider
      value={{
        campaign,
        setCampaign,
        pageStep,
        progress,
        changePageStep,
        pageResult,
        setPageResult,
        sendPageEvent,
        campaignCreateErrors,
        setCampaignCreateErrors,
        copyCampaign,
        resetCampaign,
        creationType,
        isExpenseCampaignAllowed,
        handleSkipExpenseInfoDialog,
        shouldOpenExpenseInfoDialog,
        sessionId,
      }}
    >
      {props.children}
    </CreateCampaignContext.Provider>
  )
}

export const useCreateCampaign = ({ onCreateCampaign }: { onCreateCampaign: (e?: unknown) => Promise<void> }) => {
  const context = useContext(CreateCampaignContext)
  const { campaign, sendPageEvent, setCampaignCreateErrors, setPageResult, sessionId } = context
  const [createCampaign] = useCreateCampaignMutation({
    context: {
      headers: {
        // apollo v3.7以降graphql-uploadを使う場合は、プリフライトのないリクエストをブロックするためheaderを追加する必要がある
        // https://www.apollographql.com/docs/apollo-server/security/cors/#graphql-upload
        'apollo-require-preflight': true,
      },
    },
  })
  const [loading, setLoading] = useState(false)
  const createCampaignFunc = async () => {
    setLoading(true)
    try {
      const input = await convertCreateCampaignStateToCampaignCreateParam(campaign)
      const result = await createCampaign({
        variables: {
          input,
        },
        // 公開前キャンペーンを取得して、ページのキャッシュを更新する
        refetchQueries: [
          {
            query: WaitingCampaignListDocument,
            variables: waitingCampaignInput,
          },
          {
            query: AvailableTicketsDocument,
            variables: {
              category:
                campaign.category === CampaignCategory.Product
                  ? TicketModelCategory.Remote
                  : TicketModelCategory.Locale,
            },
          },
          {
            query: AvailableAndRevisionCampaignsDocument,
          },
          {
            query: IssuedTicketsDocument,
          },
        ],
      })

      eventPush('create_campaign_method', {
        method: campaignCreationMethods.new,
      })

      // キャンペーン審査提出イベントを送信
      const campaignId = result.data?.createCampaign.id
      if (campaignId) {
        await getEventClient().sendCampaignSubmit({
          custom: {
            session_id: sessionId,
            campaign_id: decodeId(campaignId).id,
          },
        })
      }

      const nextPage = 'create_success'
      sendPageEvent(nextPage)
      setPageResult(nextPage)
      await onCreateCampaign()
    } catch (e: unknown) {
      if (e instanceof ApolloError) {
        setCampaignCreateErrors(e)
      }
      const nextPage = 'create_failure'
      sendPageEvent(nextPage)
      setPageResult(nextPage)
      await onCreateCampaign(e)
      logger.error(new Error('Failed to create campaign', { cause: e }))
    }
  }

  return {
    createCampaign: createCampaignFunc,
    loading,
  }
}
